import {FILE_UPLOAD_OPERATION_ALL_FILES_DIALOG, useAppDispatch, useAppState} from "../app/AppContext";
import React, {lazy, Suspense, useCallback, useEffect, useRef, useState} from "react";
import Box from "@mui/material/Box";
import LinearProgress from "@mui/material/LinearProgress";
import Button from "@mui/material/Button";
import {cancelOperation} from "./cancelOperation";
import {clientLog} from "../clientLog";
import Typography from "@mui/material/Typography";
import {FileConversionStatus} from "./FileConversionStatus";
import {useDocumentTranslation} from "../documents/documentContext";
import CloseIcon from "@mui/icons-material/Close";
import {Stack} from "@mui/material";
import {
    getHrStatusDetails,
    getOperationLastUpdate,
    getOperationsByType,
    getProgress,
    isOperationDone,
    isWorkerMessagesPresent
} from "./operationsPopperFunctions";
import {useSnackbar} from "notistack";

// Lazy-loaded: DialogListFiles pulls in material-react-table, @tanstack/table-core
// and related metadata-editing forms (~450 kB unminified). OperationsPopper is
// mounted unconditionally in App.jsx, so a static import here previously forced
// all of that into the app's main entry bundle even though the dialog is only
// opened when a user clicks "Se igjennom" on a finished upload operation.
const DialogListFiles = lazy(() =>
    import("../damsfileupload3/metadata/DialogListFiles").then(m => ({default: m.DialogListFiles}))
);

/**
 * OperationsPopper component displays a popper with active file upload operations.
 * @returns {false|React.JSX.Element}
 * @constructor
 */
export const OperationsPopper = () => {
    const t = useDocumentTranslation();
    const {operations} = useAppState();
    const appDispatch = useAppDispatch();

    const {enqueueSnackbar} = useSnackbar();

    const [closedByUser, setClosedByUser] = useState(false);
    const [showActiveOperations, setShowActiveOperations] = useState(false);
    const [activeOperations, setActiveOperations] = useState([]);

    const [viewedFileOperations, setViewedFileOperations] = useState([]);

    // Persist timeout IDs across renders so they can be cleared reliably.
    const reopenTimeoutRef = useRef(null);   // 5-minute reopen timeout
    const resetClosedTimeoutRef = useRef(null); // 200 ms reset timeout

    // Clear all pending timeouts when the component unmounts.
    useEffect(() => {
        return () => {
            clearTimeout(reopenTimeoutRef.current);
            clearTimeout(resetClosedTimeoutRef.current);
        };
    }, []);

    /**
     * Handles the click event for the "Vis alle filer" button.
     *
     * When the button is clicked, the `fileUploadOperationAllFilesDialog` action is dispatched with the operation ID and the `open` property set to `true`.
     * This will open the dialog showing all files for the operation.
     *
     * @private
     */
    const handleViewAllFilesClick = (operation, lastUpdate) => {
        if (!operation) {
            return;
        }
        const {jobId} = operation;
        if (!jobId) {
            return;
        }

        const failedConversion = lastUpdate?.details?.failed?.map(failed => (failed['dms_id'])) || [];
        setViewedFileOperations([...viewedFileOperations, jobId]);
        appDispatch({
            type: FILE_UPLOAD_OPERATION_ALL_FILES_DIALOG,
            data: {
                open: true,
                operationId: jobId,
                failedConversion: failedConversion
            }
        });
    };

    /**
     * Handles the click event for the "Lukk" button on the file upload operation error dialog.
     *
     * When the button is clicked, the `CANCEL_OPERATION` action is dispatched with the operation to cancel.
     * This will cancel the operation and remove it from the list of operations.
     *
     * @private
     */
    const handleErrorClick = (operation) => {
        cancelOperation(appDispatch, operation, (cancelledOperation) => {
            clientLog('info', `operation: ${cancelledOperation['jobId']} - cancelled by the user`, 'fileuploadoperationactionbuttons');
        });
    };

    /**
     * Toggles the visibility of the list of active operations.
     *
     * If the list is visible, this will hide it and vice versa.
     *
     * @private
     */
    const toggleOperationsList = () => {
        // Clear any pending reopen timeout so a second click does not cause
        // the popper to reopen unexpectedly after being manually closed.
        if (closedByUser) {
            clearTimeout(reopenTimeoutRef.current);
            reopenTimeoutRef.current = null;
        }

        if (showActiveOperations) {
            setClosedByUser(true);
            setShowActiveOperations(false);
            clearTimeout(reopenTimeoutRef.current);
            reopenTimeoutRef.current = setTimeout(() => {
                setShowActiveOperations(operations.length > 0);
                setClosedByUser(false);
                reopenTimeoutRef.current = null;
            }, 5 * 60 * 1000);
        }

        if (!showActiveOperations && !closedByUser) {
            setShowActiveOperations(true);
        } else {
            clearTimeout(resetClosedTimeoutRef.current);
            resetClosedTimeoutRef.current = setTimeout(() => {
                setClosedByUser(false);
                resetClosedTimeoutRef.current = null;
            }, 200);
        }
    };

    /**
     * Handles the case when a file upload operation times out.
     *
     * When a file upload operation times out, this function is called. It logs an error message to the console,
     * indicating that the operation timed out, and displays a snackbar with an error message.
     *
     * @function handleFileUploadTimeout
     */
    const handleFileUploadTimeout = (jobId, lastUpdate) => {
        clientLog('error', `${jobId} ${lastUpdate.id} timed out`, 'fileuploadoperation');
        enqueueSnackbar(t('fileUploadTimeoutMessage', 'Filopplastingen tar for lang tid - avbrutt'), {variant: 'error'});
    };

    /**
     * Handles the case when a file upload operation fails.
     *
     * When a file upload operation fails, this function is called with the job ID and the last update message.
     * The function logs an error message to the console, indicating that the operation failed, and displays a snackbar with an error message.
     *
     * @param {object} o - The operation that failed.
     * @param {object} lastUpdate - The last update message sent from the worker.
     * @param {object} details - The details of the failure.
     */
    const handleFileUploadError = (o, lastUpdate, details) => {
        const {jobId} = o;
        clientLog('error', `${jobId} ${lastUpdate.id} failed: ${JSON.stringify(details)}`, 'fileuploadoperation');
        enqueueSnackbar(t('fileUploadErrorMessage', 'Det oppstod en ukjent feil'), {variant: 'error'});
        cancelOperation(appDispatch, o, (cancelledOperation) => {
            clientLog('info',
                `operation: ${cancelledOperation['jobId']} - cancelled due to an error`,
                'operationspoppernew');
        });
    };

    /**
     * Returns a list of JSX elements representing the file upload operations that are
     * in progress. Each element is a box that contains the number of files in the
     * operation, the status of the operation, and a progress bar representing the
     * progress of the operation. If the operation has timed out, a 'Lukk' button is
     * displayed. If the operation is complete, a 'Se igjennom' button is displayed.
     *
     * @returns {JSX.Element} A list of JSX elements representing the file upload operations
     * that are in progress.
     */
    const getFileUploadOperations = () => {
        if (!operations || operations.length === 0) {
            return <></>;
        }

        const fileUploadOperations = getOperationsByType(operations, 'FILE_UPLOAD');
        if (fileUploadOperations.length === 0) {
            return <></>;
        }

        return <Box sx={{marginTop: '8px'}}>
            <Typography variant="caption" sx={{marginBottom: '4px'}}>
                {t('fileUploadUploadTitle', 'Opplasting')}
            </Typography>
            {fileUploadOperations.map(o => {
                let allDone;
                const {
                    jobId,
                    files = [],
                    workerMessages
                } = o;
                const lastUpdate = getOperationLastUpdate(operations, workerMessages);
                const {timedOut = false, details = {}, status, id} = lastUpdate;

                // We're only interested in the files successfully uploaded.
                const uploadedFiles = files.filter(f => f.status === 'ok');

                if (timedOut) {
                    handleFileUploadTimeout(jobId, lastUpdate);
                }

                if (status === 'error') {
                    handleFileUploadError(o, lastUpdate, details);
                }

                const hrStatusDetails = getHrStatusDetails(t, timedOut, lastUpdate, uploadedFiles);
                const progress = getProgress(uploadedFiles, lastUpdate);

                allDone = (progress === 100 && status === 'done' && id === 'gettingobjectstatus')
                    || (status === 'done' && id === 'operationcomplete');

                return <Box key={`upload-operation-${jobId}`} sx={{borderBottom: 'solid 1px #bbb', padding: '8px'}}>
                    <Box>
                        {hrStatusDetails.join(' ')}
                    </Box>
                    <Box>
                        <LinearProgress variant="determinate" value={allDone ? 100 : progress}/>
                    </Box>
                    <Box>
                        <FileConversionStatus conversionDetails={lastUpdate} t={t}/>
                    </Box>
                    <Box>
                        {timedOut && <Button key={`close-${jobId}`}
                                             onClick={() => handleErrorClick(o)}>
                            {t('fileUploadBtnClose', 'Lukk')}
                        </Button>}
                        {allDone && <Button key={`view-all-files-${jobId}`}
                                            disabled={viewedFileOperations.includes(jobId)}
                                            onClick={() => handleViewAllFilesClick(o, lastUpdate)}>
                            {t('fileUploadBtnBrowse', 'Se igjennom')}
                        </Button>}
                    </Box>
                </Box>
            })}
        </Box>
    };

    /**
     * Check if at least one file upload operation is complete. If so, display list of operations.
     *
     * This function goes through all file upload operations and checks if any of them is complete.
     * If at least one is complete, then the list of operations is displayed.
     *
     * @returns {undefined}
     */
    const atLeastOneFileUploadOperationDone = useCallback(() => {
        if (!operations || !getOperationsByType(operations, 'FILE_UPLOAD').length) {
            return;
        }
        const done = getOperationsByType(operations, 'FILE_UPLOAD').some(o => isOperationDone(o));
        if (done && !showActiveOperations && !closedByUser) {
            setShowActiveOperations(true);
        }
    }, [operations, showActiveOperations, closedByUser]);

    /**
     * Hook used to update the list of active operations.
     */
    useEffect(() => {
        if (!operations || operations.length === 0) {
            return;
        }
        atLeastOneFileUploadOperationDone();
        setActiveOperations(operations);
    }, [operations, atLeastOneFileUploadOperationDone]);

    // When worker messages are present for one or more operations, display the information box,
    // otherwise hide it!
    return isWorkerMessagesPresent(operations) &&
        <Box
            sx={{
                backgroundColor: 'white',
                border: 'solid 1px #bbb',
                borderRadius: '0px 0px 4px 4px',
                padding: '8px',
                cursor: 'pointer',
                position: 'absolute',
                top: '64px',
                right: '39px',
                zIndex: 999,
                minWidth: '300px'
            }}
            onClick={toggleOperationsList}>
            <Stack
                direction="row"
                sx={{
                    justifyContent: "space-between",
                    alignItems: "center"
                }}>
                <Box>
                    {t('fileUploadActiveOperations', 'Aktive operasjoner')}: {activeOperations.length}
                </Box>
                {showActiveOperations && <CloseIcon color="secondary"/>}
            </Stack>
            {showActiveOperations && getFileUploadOperations()}
            <Suspense fallback={null}>
                <DialogListFiles/>
            </Suspense>
        </Box>;
};